/// <amd-module name="Repos/Views/CreatePullRequest/Components/CreatePullRequestHeader" />
import * as React from "react";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { RepositoryDropdown } from "Repos/RepoDropdown/Components/RepositoryDropdown";
import { IRepositoryDropdownItem } from "Repos/RepoDropdown/RepositoryDropdownProvider";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import * as Resources from "Repos/Views/CreatePullRequest/Resources";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { CustomHeader } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ICreatePullRequestViewService, IPullRequestSourceTargetInfo } from "../CreatePullRequestViewService";

export class CreatePullRequestHeader extends VssComponent<{}> {
    public render() {
        const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");
        return (
            <CustomHeader className="repos-pr-create-header">
                <div className="flex-column rhythm-vertical-8 flex-grow">
                    <div className="repos-pr-create-header-first-row title-m" role="heading" aria-level={1}>
                        {Resources.NewPullRequest}
                    </div>
                    <ReposMessages className="flex-noshrink" showPullRequestSuggestions={false} />
                    <Observer sourceTargetInfo={createPRService.getSourceTargetInfo()}>
                        {(props: { sourceTargetInfo: IPullRequestSourceTargetInfo }) => {
                            const sourceTargetInfo = props.sourceTargetInfo;
                            return (
                                <FormatComponent className="flex-row flex-center flex-wrap" format={Resources.CreateSourceTargetFormat}>
                                    <span className="margin-right-8">
                                        {sourceTargetInfo.repositories.length > 1 && (
                                            <RepositoryDropdown
                                                onSelect={this.onSourceRepositoryChanged}
                                                providerOptions={{
                                                    repositories: sourceTargetInfo.repositories
                                                }}
                                                selectedRepositoryItem={{
                                                    name: sourceTargetInfo.srcRepository.name,
                                                    id: sourceTargetInfo.srcRepository.id,
                                                    isFork: sourceTargetInfo.srcRepository.isFork,
                                                    repository: sourceTargetInfo.srcRepository
                                                }}
                                            />
                                        )}
                                        <VersionDropdown
                                            initiallySelectedVersion={
                                                sourceTargetInfo.srcBranch ? branchToVersionDescriptor(sourceTargetInfo.srcBranch) : undefined
                                            }
                                            key={sourceTargetInfo.srcRepository.id + sourceTargetInfo.srcBranch}
                                            onSelect={this.onSourceBranchChanged}
                                            placeholder={Resources.SelectSourceBranch}
                                            providerOptions={{
                                                repository: sourceTargetInfo.srcRepository,
                                                viewTagsPivot: false
                                            }}
                                        />
                                    </span>
                                    <span className="margin-left-8">
                                        {sourceTargetInfo.repositories.length > 1 && (
                                            <RepositoryDropdown
                                                onSelect={this.onTargetRepositoryChanged}
                                                providerOptions={{
                                                    repositories: sourceTargetInfo.repositories
                                                }}
                                                selectedRepositoryItem={{
                                                    name: sourceTargetInfo.tgtRepository.name,
                                                    id: sourceTargetInfo.tgtRepository.id,
                                                    isFork: sourceTargetInfo.tgtRepository.isFork,
                                                    repository: sourceTargetInfo.tgtRepository
                                                }}
                                            />
                                        )}
                                        <VersionDropdown
                                            className="margin-right-8"
                                            initiallySelectedVersion={
                                                sourceTargetInfo.tgtBranch ? branchToVersionDescriptor(sourceTargetInfo.tgtBranch) : undefined
                                            }
                                            key={sourceTargetInfo.tgtRepository.id + sourceTargetInfo.tgtBranch}
                                            onSelect={this.onTargetBranchChanged}
                                            placeholder={Resources.SelectTargetBranch}
                                            providerOptions={{
                                                viewTagsPivot: false,
                                                repository: sourceTargetInfo.tgtRepository
                                            }}
                                        />
                                        <Button
                                            ariaLabel={Resources.SwitchSourceAndTarget}
                                            className="icon-only"
                                            onClick={this.switchBranches}
                                            subtle={true}
                                            tooltipProps={{ text: Resources.SwitchSourceAndTarget }}
                                        >
                                            <Icon iconName="Switch" />
                                        </Button>
                                    </span>
                                </FormatComponent>
                            );
                        }}
                    </Observer>
                </div>
            </CustomHeader>
        );
    }

    private onSourceRepositoryChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IRepositoryDropdownItem>) => {
        if (item && item.data) {
            const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");
            createPRService.setSourceTargetInfo({
                srcRepository: item.data.repository,
                srcBranch: refToBranchName(item.data.repository!.defaultBranch) || ""
            });
        }
    };

    private onTargetRepositoryChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IRepositoryDropdownItem>) => {
        if (item && item.data) {
            const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");
            createPRService.setSourceTargetInfo({
                tgtRepository: item.data.repository,
                tgtBranch: refToBranchName(item.data.repository!.defaultBranch) || ""
            });
        }
    };

    private onSourceBranchChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem> | null): void => {
        if (item && item.data) {
            const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");
            createPRService.setSourceTargetInfo({ srcBranch: item.data.versionDescriptor.version });
        }
    };

    private onTargetBranchChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem> | null): void => {
        if (item && item.data) {
            const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");
            createPRService.setSourceTargetInfo({ tgtBranch: item.data.versionDescriptor.version });
        }
    };

    private switchBranches = () => {
        const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");
        const srcTgt = createPRService.getSourceTargetInfo().value;
        createPRService.setSourceTargetInfo({
            srcBranch: srcTgt.tgtBranch,
            srcRepository: srcTgt.tgtRepository,
            tgtBranch: srcTgt.srcBranch,
            tgtRepository: srcTgt.srcRepository
        });
    };
}
